import type {
  TermsOfTransition,
  TermsOfTransitionAction,
} from 'types/course/termsOfTransition';
import { isPassingTransitionAction } from 'shared/composables/useTermsOfTransition';
import type { MessageSchema } from 'src/boot/i18n';

type Translate = (
  key: keyof MessageSchema,
  params?: Record<string, unknown>,
) => string;

/** Lowest score that moves the student forward, or null if no rule does. */
export function stepPassScore(terms: TermsOfTransition[] | undefined) {
  const passing = (terms ?? []).filter((term) =>
    isPassingTransitionAction(term.action),
  );
  return passing.length ? Math.min(...passing.map((term) => term.from)) : null;
}

/**
 * Banner text for a step that sends the student back (Go_To_Previous) or has
 * them redo it (Redo_Current) when the CMS rule has no result_feedback: their
 * score, the score that moves them on, and what happens next. null for any
 * other action. Go_To_Previous on the first step redoes it, so it reads as a
 * redo.
 */
export function stepRetryFeedback({
  action,
  score,
  terms,
  isFirstStep,
  t,
}: {
  action: TermsOfTransitionAction | undefined;
  score: number;
  terms: TermsOfTransition[] | undefined;
  isFirstStep: boolean;
  t: Translate;
}): { title: string; message: string } | null {
  if (action !== 'Go_To_Previous' && action !== 'Redo_Current') return null;
  const goesBack = action === 'Go_To_Previous' && !isFirstStep;
  const passScore = stepPassScore(terms);
  const message = [
    t('STEP_FEEDBACK_SCORE', { score: Math.round(score) }),
    passScore === null
      ? ''
      : t('STEP_FEEDBACK_PASS_SCORE', { score: passScore }),
    t(
      goesBack ? 'STEP_FEEDBACK_GO_BACK_MESSAGE' : 'STEP_FEEDBACK_REDO_MESSAGE',
    ),
  ];
  return {
    title: t(
      goesBack ? 'STEP_FEEDBACK_GO_BACK_TITLE' : 'STEP_FEEDBACK_REDO_TITLE',
    ),
    message: message.filter(Boolean).join(' '),
  };
}
